import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Target,
  TrendingUp,
  Zap,
  Users,
  Star,
  Check,
  ShieldCheck,
  Quote,
  Plus,
  Minus,
  Sparkles,
  Twitter,
  Instagram,
  Mail,
  MousePointerClick,
  FileText,
  DownloadCloud,
  Lock,
} from 'lucide-react';
import { books, AUTHOR, STATS, TESTIMONIALS, FAQS } from '../data/books';
import BookCard from './BookCard';
import './BooksGrid.css';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const DISCOVER = [
  {
    icon: Target,
    title: 'Strategic AI Implementation',
    text: 'Integrate AI into a real business strategy for measurable impact and ROI — not random tools you abandon in a week.',
  },
  {
    icon: TrendingUp,
    title: 'Wealth-Building Systems',
    text: 'Proven frameworks to build multiple income streams and turn AI leverage into lasting, generational wealth.',
  },
  {
    icon: Zap,
    title: 'Automation & Efficiency',
    text: 'Automate the repetitive work that drains your time and scale your operations without hiring an army.',
  },
  {
    icon: Users,
    title: 'Leadership & Ministry',
    text: 'How leaders and pastors expand their reach, deepen their impact, and steward their time in the digital age.',
  },
];

const STEPS = [
  {
    icon: MousePointerClick,
    title: 'Choose your playbook',
    text: 'Pick the book that matches your goal — wealth, business, income or ministry. You’ll get all five either way.',
  },
  {
    icon: FileText,
    title: 'Enter your details',
    text: 'Tell us where to send access. It takes under 30 seconds — no payment and no credit card required.',
  },
  {
    icon: DownloadCloud,
    title: 'Read & apply today',
    text: 'Your secure library opens instantly. Download every book to your phone or laptop and start building.',
  },
];

const BooksGrid = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const startFunnel = () => navigate(`/preview/${books[0].id}`);

  return (
    <main className="lp">
      {/* ============ ANNOUNCEMENT BAR ============ */}
      <div className="announce">
        <div className="container announce-inner">
          <Sparkles size={15} />
          <span>
            Free for a limited time — <strong>5 complete AI playbooks</strong>, instant access
          </span>
        </div>
      </div>

      {/* ============ NAV ============ */}
      <header className="nav">
        <div className="container nav-inner">
          <button className="brand" onClick={() => scrollTo('top')} aria-label="AI Wealth Library home">
            <span className="brand-mark">AI</span>
            <span className="brand-text">
              The AI Wealth Library
              <em>by Dr. Stephen Akintayo</em>
            </span>
          </button>
          <nav className="nav-links">
            <button onClick={() => scrollTo('library')}>The Books</button>
            <button onClick={() => scrollTo('author')}>The Author</button>
            <button onClick={() => scrollTo('faq')}>FAQ</button>
          </nav>
          <button className="btn btn-primary nav-cta" onClick={startFunnel}>
            Get free access
            <ArrowRight size={17} />
          </button>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="hero" id="top">
        <div className="hero-glow" aria-hidden="true" />
        <div className="container hero-inner">
          <motion.div
            className="hero-copy"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.span className="eyebrow" variants={fadeUp}>
              The Complete AI Wealth Library
            </motion.span>

            <motion.h1 className="hero-title" variants={fadeUp}>
              Turn Artificial Intelligence into
              <span className="accent-text"> Income, Influence &amp; Wealth</span>
            </motion.h1>

            <motion.p className="hero-sub" variants={fadeUp}>
              Five complete playbooks — over 1,250 pages of field-tested strategy — showing anyone,
              with or without a tech background, how to build real businesses and lasting wealth in
              the AI era. Yours free.
            </motion.p>

            <motion.div className="hero-actions" variants={fadeUp}>
              <button className="btn btn-primary btn-lg" onClick={startFunnel}>
                Get all 5 books free
                <ArrowRight size={19} />
              </button>
              <button className="btn btn-outline btn-lg" onClick={() => scrollTo('library')}>
                <BookOpen size={19} />
                See what's inside
              </button>
            </motion.div>

            <motion.div className="hero-proof" variants={fadeUp}>
              <div className="proof-avatars" aria-hidden="true">
                {['#0E8A63', '#333B4C', '#0B6F50', '#1D2330'].map((c, i) => (
                  <span key={i} className="proof-avatar" style={{ background: c }}>
                    {['A', 'C', 'E', 'T'][i]}
                  </span>
                ))}
              </div>
              <div className="proof-text">
                <div className="proof-stars" aria-hidden="true">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <span>
                  Joined by <strong>25,000+</strong> ambitious readers across Africa &amp; beyond
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Fanned book covers */}
          <motion.div
            className="hero-stack"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          >
            <div className="stack-wrap">
              {books.map((book, i) => (
                <motion.img
                  key={book.id}
                  src={book.coverImage}
                  alt={`${book.title} cover`}
                  className={`stack-cover stack-cover-${i}`}
                  initial={{ opacity: 0, y: 40, rotate: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.3 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              ))}
              <div className="stack-badge">
                <span className="stack-badge-num">5</span>
                <span className="stack-badge-label">Books<br />in 1 library</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ STAT STRIP ============ */}
      <section className="stat-strip">
        <div className="container stat-strip-inner">
          {STATS.map((s, i) => (
            <motion.div
              className="strip-stat"
              key={s.label}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
            >
              <span className="strip-num">{s.number}</span>
              <span className="strip-label">{s.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ LIBRARY ============ */}
      <section className="library" id="library">
        <div className="container">
          <motion.div
            className="section-head"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <span className="eyebrow eyebrow--center">Your AI Success Library</span>
            <h2>Five playbooks. One unfair advantage.</h2>
            <p>
              Each book is a complete, standalone system. Together, they’re the blueprint the next
              generation of AI-powered entrepreneurs will be built on.
            </p>
          </motion.div>

          <div className="books-grid">
            {books.map((book, index) => (
              <BookCard key={book.id} book={book} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ DISCOVER ============ */}
      <section className="discover">
        <div className="container">
          <motion.div
            className="section-head"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <span className="eyebrow eyebrow--center">What You Will Discover</span>
            <h2>Not hype. A working system.</h2>
            <p>Every chapter is built to be applied — the same frameworks used to build real companies.</p>
          </motion.div>

          <div className="discover-grid">
            {DISCOVER.map((d, i) => (
              <motion.article
                className="discover-card"
                key={d.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
              >
                <div className="discover-icon">
                  <d.icon size={24} strokeWidth={1.75} />
                </div>
                <h3>{d.title}</h3>
                <p>{d.text}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AUTHOR / AUTHORITY ============ */}
      <section className="author" id="author">
        <div className="container author-inner">
          <motion.div
            className="author-portrait"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <div className="portrait-frame">
              <img
                src="/images/stephen-akintayo.webp"
                alt="Dr. Stephen Akintayo, Chairman of GText Holdings"
                className="portrait-photo"
                loading="lazy"
              />
              <span className="portrait-badge">
                <ShieldCheck size={16} />
                Verified author
              </span>
            </div>
            <div className="portrait-caption">
              <span className="signature">Dr. Stephen Akintayo</span>
              <span className="signature-role">Chairman, GText Holdings</span>
            </div>
          </motion.div>

          <motion.div
            className="author-copy"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <span className="eyebrow eyebrow--light">Meet Your Guide</span>
            <h2>Learn from one of Africa’s foremost investment coaches.</h2>
            {AUTHOR.bio.map((p, i) => (
              <p key={i} className="author-para">
                {p}
              </p>
            ))}
            <ul className="cred-list">
              {AUTHOR.credentials.map((c) => (
                <li key={c}>
                  <ShieldCheck size={18} />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="testimonials">
        <div className="container">
          <motion.div
            className="section-head"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <span className="eyebrow eyebrow--center">From Real Readers</span>
            <h2>Results that speak louder than promises.</h2>
          </motion.div>

          <div className="testimonial-grid">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                className="testimonial-card"
                key={t.name}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
              >
                <Quote className="testimonial-quote" size={30} />
                <div className="testimonial-stars" aria-label="5 out of 5 stars">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={16} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <blockquote>{t.quote}</blockquote>
                <figcaption>
                  <span className="testimonial-name">{t.name}</span>
                  <span className="testimonial-role">{t.role}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="how">
        <div className="container">
          <motion.div
            className="section-head"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <span className="eyebrow eyebrow--center">How It Works</span>
            <h2>From this page to your first win in 3 steps.</h2>
          </motion.div>

          <div className="how-grid">
            {STEPS.map((step, i) => (
              <motion.div
                className="how-step"
                key={step.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
              >
                <span className="how-num">{String(i + 1).padStart(2, '0')}</span>
                <div className="how-icon">
                  <step.icon size={22} strokeWidth={1.75} />
                </div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="faq" id="faq">
        <div className="container-narrow">
          <motion.div
            className="section-head"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <span className="eyebrow eyebrow--center">Questions, Answered</span>
            <h2>Everything you might be wondering.</h2>
          </motion.div>

          <div className="faq-list">
            {FAQS.map((item, i) => {
              const open = openFaq === i;
              return (
                <div className={`faq-item ${open ? 'faq-open' : ''}`} key={item.q}>
                  <button
                    className="faq-q"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                  >
                    <span>{item.q}</span>
                    {open ? <Minus size={20} /> : <Plus size={20} />}
                  </button>
                  <div className="faq-a" style={{ maxHeight: open ? '260px' : '0' }}>
                    <p>{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="final-cta">
        <div className="final-glow" aria-hidden="true" />
        <div className="container-narrow final-inner">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <span className="eyebrow eyebrow--light eyebrow--center">This Is Your Moment</span>
            <h2 className="final-title">
              The AI revolution is here. The people who act now will lead it.
            </h2>
            <p className="final-sub">
              Don’t spend the next year guessing. Download the complete blueprint, apply it, and put
              yourself years ahead of everyone still waiting on the sidelines.
            </p>
            <button className="btn btn-primary btn-lg" onClick={startFunnel}>
              Claim your 5 free books
              <ArrowRight size={19} />
            </button>
            <div className="final-assurances">
              <span><Check size={15} /> Instant access</span>
              <span><Lock size={15} /> 100% free — no card</span>
              <span><ShieldCheck size={15} /> No spam, ever</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="brand">
                <span className="brand-mark">AI</span>
                <span className="brand-text">
                  The AI Wealth Library
                  <em>by Dr. Stephen Akintayo</em>
                </span>
              </div>
              <p>
                Five practical playbooks to help you build income, influence and lasting wealth in
                the age of artificial intelligence.
              </p>
            </div>

            <div className="footer-col">
              <h4>Get In Touch</h4>
              <a href="mailto:products@stephenakintayo.com" className="footer-link">
                <Mail size={16} />
                products@stephenakintayo.com
              </a>
            </div>

            <div className="footer-col">
              <h4>Follow</h4>
              <a
                className="footer-link"
                href="https://twitter.com/stephenakintayo"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Twitter size={16} /> @stephenakintayo
              </a>
              <a
                className="footer-link"
                href="https://instagram.com/stephenakintayo"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram size={16} /> @stephenakintayo
              </a>
            </div>

            <div className="footer-col">
              <h4>Start Now</h4>
              <button className="btn btn-primary footer-cta" onClick={startFunnel}>
                Get free access
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} SA Foundation / GText Holdings. All rights reserved.</p>
            <p className="disclaimer">
              This site is not affiliated with or endorsed by any specific AI platform. Results may
              vary based on individual effort and market conditions.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default BooksGrid;
